import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import InputLabel from "@mui/material/InputLabel";
import LinearProgress from "@mui/material/LinearProgress";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { DEFAULT_SCAN_WINDOW_HOURS, SCAN_WINDOW_OPTIONS_HOURS } from "./constants";
import { useFleetCheckColumns } from "./hooks/useFleetCheckColumns";
import { useFleetCheckRows } from "./hooks/useFleetCheckRows";
import { useGpsOdometerSetting } from "./hooks/useGpsOdometerSetting";
import { FleetCheckRow } from "./models/fleetCheckRow";
import { isFleetCheckExportDisabled } from "./utilities/isFleetCheckExportDisabled";
import { I18nGrid } from "@/components/data/I18nGrid";
import { ExportToolbar } from "@/components/grid/ExportToolbar";
import { OdometerDecimalsButton } from "@/components/settings/OdometerDecimalsButton";
import { RESULTS_LIMIT_DEVICE } from "@/constants/resultsLimits";
import { useDebounced } from "@/hooks/useDebounced";
import { useOdometerDecimals } from "@/hooks/userState/useOdometerDecimals";
import { LiveDataChecklist } from "@/pages/live-data/components/LiveDataChecklist";
import { isGpsOdometerRow } from "@/pages/live-data/utilities/diagnosticMatchers";

const GRID_SLOTS = { toolbar: ExportToolbar };
const FRACTION_DIGITS = { rawOdometer: 1, odometer: 1, gpsOdometer: 1 };

/**
 * The installation checklist, asked of the whole fleet at once.
 *
 * The scan is started by hand rather than on mount: it walks every vehicle's readings over the
 * chosen window, which is far too expensive to spend because someone opened the page. Each row
 * expands into the same checklist the single-vehicle page shows.
 */
export default function VehicleDataCheckPage() {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  // Debounced, because every change asks Geotab for the device list again.
  const searchDebounced = useDebounced(search, 300);
  const [onlyIssues, setOnlyIssues] = useState(false);
  const [hoursBack, setHoursBack] = useState<number>(DEFAULT_SCAN_WINDOW_HOURS);
  const gpsOdometer = useGpsOdometerSetting();

  const {
    rows,
    deviceCount,
    isLoading,
    isTruncated,
    scan,
    start,
    stop,
    diagnosticsLoading,
    diagnosticsTruncated,
    lookupsLoading,
    enrichmentLoading,
    errors,
  } = useFleetCheckRows(searchDebounced, hoursBack, onlyIssues, gpsOdometer.enabled);

  const exportDisabled = isFleetCheckExportDisabled({
    scanWindow: scan.window,
    isScanRunning: scan.isRunning,
    scanFailed: scan.error !== null,
    rowCount: rows.length,
    lookupsLoading,
    enrichmentLoading,
    errors,
  });

  const { decimals: odometerDecimals } = useOdometerDecimals();
  const columns = useFleetCheckColumns(odometerDecimals, gpsOdometer.enabled);

  const detailPanelContent = useCallback(
    ({ row }: { row: FleetCheckRow }) => (
      <Box sx={{ p: 1.5 }}>
        {row.status === "notScanned" ? (
          // No readings were looked at, so a checklist would show every check as failed.
          <Typography color="text.secondary" variant="body2">
            {t("vehicleDataCheck.notScannedDetail")}
          </Typography>
        ) : (
          <LiveDataChecklist
            device={row.device}
            rows={row.rows}
            gpsOdometerRows={row.rows.filter(isGpsOdometerRow)}
          />
        )}
      </Box>
    ),
    [t],
  );
  const detailPanelHeight = useCallback(() => "auto" as const, []);

  // The window the scan actually covered, not one derived from the current setting: the setting
  // can be changed after a scan, and then the sheet would name a period the numbers in it never
  // came from. The vehicle search is the page's own, so the grid's quick filter stays hidden.
  const slotProps = useMemo(
    () => ({
      toolbar: {
        report: {
          title: t("vehicleDataCheck.title"),
          period: scan.window ? { from: scan.window.from, to: scan.window.to } : undefined,
        },
        fractionDigits: FRACTION_DIGITS,
        exportDisabled,
        hideQuickFilter: true,
      },
    }),
    [exportDisabled, scan.window, t],
  );

  return (
    <Stack spacing={2} sx={{ p: { xs: 1, md: 2 }, height: "100%" }}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={2} justifyContent="space-between">
        <Typography color="text.secondary" variant="body2">
          {t(
            "vehicleDataCheck.subtitle",
            "The installation checklist, asked of every vehicle at once",
          )}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 160 }} disabled={scan.isRunning}>
            <InputLabel>{t("vehicleDataCheck.timeWindow")}</InputLabel>
            <Select
              value={hoursBack}
              label={t("vehicleDataCheck.timeWindow")}
              onChange={event => setHoursBack(Number(event.target.value))}
            >
              {SCAN_WINDOW_OPTIONS_HOURS.map(hours => (
                <MenuItem key={hours} value={hours}>
                  {hours === 24
                    ? t("vehicleDataCheck.windowHours", { count: hours })
                    : t("vehicleDataCheck.windowDays", { count: hours / 24 })}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip
            title={t(
              "vehicleDataCheck.gpsOdometerHint",
              "Geotab gives the GPS odometer only per vehicle: one extra call per vehicle",
            )}
          >
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={gpsOdometer.enabled}
                  onChange={event => gpsOdometer.setEnabled(event.target.checked)}
                  disabled={scan.isRunning}
                />
              }
              label={t("vehicleDataCheck.columns.gpsOdometer")}
            />
          </Tooltip>
          {scan.isRunning ? (
            <Button color="error" startIcon={<StopIcon />} onClick={stop}>
              {t("vehicleDataCheck.stopScan")}
            </Button>
          ) : (
            <Button
              variant="contained"
              startIcon={<PlayArrowIcon />}
              onClick={start}
              disabled={deviceCount === 0 || diagnosticsLoading || errors.diagnostics}
            >
              {t("vehicleDataCheck.startScan")}
            </Button>
          )}
          <OdometerDecimalsButton />
        </Stack>
      </Stack>

      {isTruncated && (
        <Alert severity="warning">
          {t(
            "vehicleDataCheck.truncated",
            "Only the first {{count}} vehicles are listed. Narrow the search or the group filter to check the rest.",
            { count: RESULTS_LIMIT_DEVICE },
          )}
        </Alert>
      )}

      {errors.devices && <Alert severity="error">{t("vehicleDataCheck.devicesFailed")}</Alert>}

      {errors.statusInfo && (
        <Alert severity="error">
          {t(
            "vehicleDataCheck.statusInfoFailed",
            "Whether the units are communicating could not be loaded; it is shown as not known.",
          )}
        </Alert>
      )}

      {errors.vins && (
        <Alert severity="error">
          {t(
            "vehicleDataCheck.vinsFailed",
            "The vehicle types could not be decoded from the VINs.",
          )}
        </Alert>
      )}

      {errors.lookups && (
        <Alert severity="error">
          {t(
            "vehicleDataCheck.lookupsFailed",
            "The diagnostic names could not be loaded. Results may wrongly show vehicles as incomplete.",
          )}
        </Alert>
      )}

      {errors.diagnostics && (
        <Alert severity="error">{t("vehicleDataCheck.diagnosticsFailed")}</Alert>
      )}

      {diagnosticsTruncated && (
        <Alert severity="warning">{t("vehicleDataCheck.diagnosticsTruncated")}</Alert>
      )}

      {scan.error && (
        <Alert severity="error">
          {t("vehicleDataCheck.scanFailed")}: {scan.error.message}
        </Alert>
      )}

      {(scan.isRunning || scan.pagesLoaded > 0) && (
        <Box>
          {scan.isRunning && (
            <LinearProgress
              variant="determinate"
              value={
                scan.seriesTotal === 0
                  ? 0
                  : Math.min(100, (scan.seriesDone / scan.seriesTotal) * 100)
              }
            />
          )}
          <Typography variant="caption" color="text.secondary">
            {t("vehicleDataCheck.scanProgress", {
              done: scan.seriesDone,
              total: scan.seriesTotal,
              records: scan.recordsProcessed,
              devices: scan.deviceCount,
            })}
          </Typography>
        </Box>
      )}

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
        <TextField
          size="small"
          label={t("vehicleDataCheck.search")}
          value={search}
          onChange={event => setSearch(event.target.value)}
          sx={{ minWidth: { sm: 300 } }}
        />
        <Button
          variant={onlyIssues ? "contained" : "outlined"}
          onClick={() => setOnlyIssues(current => !current)}
        >
          {t("vehicleDataCheck.onlyIssues")}
        </Button>
        <Typography color="text.secondary" variant="body2" sx={{ ml: { sm: "auto" } }}>
          {t("vehicleDataCheck.vehicleCount", { count: rows.length })}
        </Typography>
      </Stack>

      <Paper sx={{ flex: 1, minHeight: 420 }}>
        <I18nGrid
          rows={rows}
          columns={columns}
          loading={isLoading}
          disableRowSelectionOnClick
          settingsKey="vehicleDataCheck"
          pagination
          pageSizeOptions={[25, 50, 100]}
          initialState={{
            density: "compact",
            pagination: { paginationModel: { pageSize: 25 } },
            pinnedColumns: { left: ["name"], right: ["status"] },
          }}
          getDetailPanelHeight={detailPanelHeight}
          getDetailPanelContent={detailPanelContent}
          slots={GRID_SLOTS}
          slotProps={slotProps}
          sx={{ border: 0 }}
        />
      </Paper>
    </Stack>
  );
}
